import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Home,
  Contact,
  Profile,
  Book,
  Magazine,
  Multimedia,
  Search,
  NotFound,
  Custom,
  Discover,
  Login,
  InfoTypes,
  Curriculum,
  ShowLink,
} from "pages";
import { ToastContainer } from "react-toastify";
import { useLanguage } from "hooks";
import { getFacts, getNews, getSlides } from "store/app";
import "styles/App.scss";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getPublicPages,
  getImportantLinks,
  getSettings,
  getSocialMediaLinks,
} from "store/app/actions";
import { checkLogin } from "store/auth/actions";

function App() {
  const { pathname } = useLocation();
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const dispatch = useDispatch();
  const { access_token, auth_checked, isLoggedIn } = useSelector(
    (state) => state.auth
  ); // call data from auth store

  React.useLayoutEffect(() => {
    document.documentElement.setAttribute("lang", language.lang);
    document.documentElement.setAttribute("dir", language.direction);
  }, [language]);

  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  React.useEffect(() => {
    dispatch(checkLogin());
    dispatch(getSlides());
    dispatch(getNews());
    dispatch(getFacts());
    dispatch(getSocialMediaLinks());
    dispatch(getPublicPages());
    dispatch(getImportantLinks());
    dispatch(getSettings());
  }, [dispatch, language, access_token]);

  return (
    <>
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={true}
        rtl={language.direction === "rtl"}
        pauseOnFocusLoss
        pauseOnHover
        theme="dark"
      />
      <Routes>
        <Route path="*" element={<NotFound />} />

        <Route
          path="/"
          element={
            auth_checked && (isLoggedIn ? <Home /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="login"
          element={!isLoggedIn ? <Login /> : <Navigate to="/" />}
        />

        <Route path="contact" element={<Contact />} />

        <Route
          path="profile"
          element={
            auth_checked &&
            (isLoggedIn ? <Profile.Edit /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="profile/history"
          element={
            auth_checked &&
            (isLoggedIn ? <Profile.History /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="profile/favourite"
          element={
            auth_checked &&
            (isLoggedIn ? <Profile.Favourite /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="curriculum"
          element={
            auth_checked &&
            (isLoggedIn ? <Curriculum /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="books"
          element={
            auth_checked &&
            (isLoggedIn ? <Book.List /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="book/:book_id"
          element={
            auth_checked &&
            (isLoggedIn ? <Book.Details /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="book/:book_id/:sub_id"
          element={
            auth_checked &&
            (isLoggedIn ? <Book.Details /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="discover/:type_id"
          element={
            auth_checked &&
            (isLoggedIn ? <Discover /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="magazines"
          element={
            auth_checked &&
            (isLoggedIn ? <Magazine.List /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="magazines/:magazine_id"
          element={
            auth_checked &&
            (isLoggedIn ? <Magazine.Details /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="multimedia"
          element={
            auth_checked &&
            (isLoggedIn ? <Multimedia /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="InfoTypes"
          element={
            auth_checked &&
            (isLoggedIn ? <InfoTypes /> : <Navigate to="/login" />)
          }
        />



        <Route
          path="search/advanced"
          element={
            auth_checked &&
            (isLoggedIn ? <Search.Advanced /> : <Navigate to="/login" />)
          }
        />

        <Route
          path="search"
          element={
            auth_checked &&
            (isLoggedIn ? <Search.Normal /> : <Navigate to="/login" />)
          }
        />


        <Route
          path="show_link/:book_id/:link_id"
          element={
            auth_checked &&
            (isLoggedIn ? <ShowLink /> : <Navigate to="/login" />)
          }
        />

        <Route path="page/:machine_name" element={<Custom />} />
      </Routes>
    </>
  );
}

export default App;
